import {
  ArrowRight,
  CalendarDays,
  type LucideIcon,
  Pause,
  Phone,
  PhoneOff,
  RotateCcw,
  Send,
} from 'lucide-react';

import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import {
  PortalDialog,
  PortalDialogContent,
  PortalDialogDescription,
  PortalDialogFooter,
  PortalDialogHeader,
  PortalDialogTitle,
} from '@/modules/ui/components/portal-dialog/PortalDialog';

import { AutomationChip } from './AutomationChip';
import {
  changedStages,
  type Consequence,
  consequencesOf,
  isHandBack,
  type JobAutomation,
  type Setup,
  stageLabels,
} from './jobAutomation';

const consequenceIcon: Record<Consequence, LucideIcon> = {
  outreachStarts: Send,
  outreachStops: Pause,
  aiCallStarts: Phone,
  aiCallStops: PhoneOff,
  bookingLinkStarts: CalendarDays,
  bookingLinkStops: PhoneOff,
};

/** The switch under review, fixed when the dialog opens so a save does not rewrite it mid-exit. */
export interface SetupReview {
  from: JobAutomation;
  to: Setup;
}

interface ConfirmSetupDialogProps {
  review: SetupReview | null;
  jobTitle: string;
  /** Whose booking link candidates get: "your" or "Ana’s". */
  bookingLinkOwner: string;
  /** The user's LinkedIn is not connected, so Finlay's outreach waits for it. */
  isLinkedInMissing: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSaving: boolean;
  error: string | null;
  onConfirm: () => void;
}

/**
 * Spells out a setup switch on a live job before it saves: which stages
 * change hands, and what that sets in motion. A failure stays in the dialog.
 */
export function ConfirmSetupDialog({
  review,
  jobTitle,
  bookingLinkOwner,
  isLinkedInMissing,
  open,
  onOpenChange,
  isSaving,
  error,
  onConfirm,
}: ConfirmSetupDialogProps) {
  const consequenceText: Record<Consequence, string> = {
    outreachStarts: isLinkedInMissing
      ? 'Once you connect LinkedIn, Finlay starts messaging matching candidates you haven’t contacted, and follows up in its own words.'
      : 'Finlay starts messaging matching candidates you haven’t contacted, and follows up in its own words.',
    outreachStops:
      'Finlay stops messaging new candidates. You contact the people you like from the shortlist.',
    aiCallStarts:
      'Candidates who reply get a 10-minute AI call. You’ll find the summary and recording on their profile.',
    aiCallStops:
      'No new AI calls are booked. Candidates go straight to your review.',
    bookingLinkStarts: `Candidates who reply get ${bookingLinkOwner} booking link. You add notes after the call.`,
    bookingLinkStops:
      'Candidates no longer get a booking link. They go straight to your review.',
  };

  const changeOpen = (next: boolean) => {
    if (isSaving) return;
    onOpenChange(next);
  };

  if (!review) return null;
  const { from, to } = review;
  const handBack = isHandBack(from, to.values);

  return (
    <PortalDialog onOpenChange={changeOpen} open={open}>
      <PortalDialogContent
        className="gap-0 overflow-hidden p-0 sm:max-w-[520px]"
        showCloseButton={false}
      >
        <PortalDialogHeader className="px-6 pt-6 pr-6">
          <PortalDialogTitle className="text-[17px]">
            Switch this job to {to.title}?
          </PortalDialogTitle>
          <PortalDialogDescription>
            These changes apply to new candidates on {jobTitle}.
          </PortalDialogDescription>
        </PortalDialogHeader>

        <div className="px-6 pt-5">
          <div className="overflow-hidden rounded-[10px] border">
            <ul className="divide-y">
              {changedStages(from, to.values).map((key) => (
                <li
                  className="flex flex-wrap items-center gap-x-2.5 gap-y-2 px-3.5 py-3"
                  key={key}
                >
                  <span className="min-w-0 flex-1 text-[13px]">
                    {stageLabels[key]}
                  </span>
                  <span className="flex items-center gap-2.5">
                    <span className="sr-only">from</span>
                    <AutomationChip stage={key} values={from} />
                    <ArrowRight
                      aria-hidden
                      className="size-3.5 text-neutral-500"
                    />
                    <span className="sr-only">to</span>
                    <AutomationChip stage={key} values={to.values} />
                  </span>
                </li>
              ))}
            </ul>
            <p className="bg-sidebar text-muted-foreground border-t px-3.5 py-2.5 text-xs">
              {to.values.outreach === 'me'
                ? 'Unchanged: Finlay keeps finding and ranking candidates for your shortlist.'
                : 'Unchanged: Finlay keeps finding candidates, and you still decide who moves forward.'}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 px-6 pt-5 pb-6">
          <h3 className="text-muted-foreground text-xs font-semibold">
            When you confirm
          </h3>
          <ul className="flex flex-col gap-3">
            {consequencesOf(from, to.values).map((consequence) => (
              <ConsequenceItem
                icon={consequenceIcon[consequence]}
                key={consequence}
                text={consequenceText[consequence]}
              />
            ))}
            <ConsequenceItem
              icon={RotateCcw}
              text={
                handBack
                  ? 'Conversations and calls already under way finish as planned. You can switch back at any time.'
                  : 'Candidates already in progress keep their current setup. You can switch back at any time.'
              }
            />
          </ul>
          {error && <InlineError>{error}</InlineError>}
        </div>

        <PortalDialogFooter className="bg-sidebar px-6 py-3.5">
          <Button
            className="h-8 rounded-md"
            disabled={isSaving}
            onClick={() => changeOpen(false)}
            size="sm"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            autoFocus
            className="h-8 rounded-md"
            loading={isSaving}
            onClick={onConfirm}
            size="sm"
          >
            {to.key === 'handsOff'
              ? 'Start Autopilot'
              : `Switch to ${to.title}`}
          </Button>
        </PortalDialogFooter>
      </PortalDialogContent>
    </PortalDialog>
  );
}

function ConsequenceItem({
  icon: Icon,
  text,
}: {
  icon: LucideIcon;
  text: string;
}) {
  return (
    <li className="flex gap-2.5 text-[13px] leading-[1.5]">
      <Icon
        aria-hidden
        className="text-muted-foreground mt-0.5 size-4 shrink-0"
      />
      {text}
    </li>
  );
}
